import React, { useMemo, useRef } from 'react';
import { SkeletonLoading } from '@saleshandy/design-system';
import {
  AlertTriangle,
  ArrowRight,
  EmailBlock,
  Inbox,
  Mail,
} from '@saleshandy/icons';
import Table from '../../../../../../../../shared/design-system/components/organisms/table';
import { PaginationShowHide } from '../../../../../../../../shared/design-system/components/organisms/table/types';
import { TestReportEspsResponse } from '../../../../../../types';
import {
  ESP_PRIORITY,
  SeedAccountLabels,
} from '../../../../../test-create-update/components/seed-esp-account/type';
import { getEspIcon } from '../utils/esp-icon';
import { useFirebaseAnimation } from '../utils/use-firebase-animation';

const ESPPlacementReport: React.FC<{
  testReportEspsData: TestReportEspsResponse;
  firebaseData?: any;
  sentCount?: number;
}> = ({ testReportEspsData, firebaseData, sentCount }) => {
  // Function to get ESP priority (higher number = lower priority)
  const getEspPriority = (esp: string): number =>
    ESP_PRIORITY[esp as keyof typeof ESP_PRIORITY] || 999;

  // Function to sort ESP pairs by priority
  const sortEspPairs = (data: any[]) =>
    data
      ? [...data].sort((a, b) => {
          const senderPriorityA = getEspPriority(a?.senderEmailType);
          const senderPriorityB = getEspPriority(b?.senderEmailType);

          // First sort by sender ESP priority
          if (senderPriorityA !== senderPriorityB) {
            return senderPriorityA - senderPriorityB;
          }

          // If sender ESP is the same, sort by recipient ESP priority
          const recipientPriorityA = getEspPriority(a?.recipientEmailType);
          const recipientPriorityB = getEspPriority(b?.recipientEmailType);

          return recipientPriorityA - recipientPriorityB;
        })
      : [];

  // Store the original order of ESP pairs to maintain consistent table order
  const originalOrderRef = useRef<string[]>([]);

  // Use Firebase animation hook
  const { shouldAnimateItem } = useFirebaseAnimation(firebaseData, {
    duration: 2000,
    fields: ['inboxCount', 'spamCount', 'otherCount', 'notDeliveredCount'],
  });

  // Helper function to calculate percentage or determine if should show 0%
  const calculatePercentage = (
    count: number | null,
    totalCount: number,
  ): number | null => {
    if (totalCount > 0) {
      return Math.round(((count || 0) / totalCount) * 100);
    }

    const placementCount = firebaseData?.summary?.placementCount || sentCount;
    const shouldShowZero = placementCount && placementCount > 0;

    return shouldShowZero ? 0 : null;
  };

  // Parse Firebase espToEsp data and transform it to match the table structure
  const getFirebaseTableData = () => {
    if (!firebaseData?.espToEsp) return null;

    const firebaseRows = [];
    const currentKeys = Object.keys(firebaseData.espToEsp);

    // If this is the first time we're getting Firebase data, store the order
    if (originalOrderRef.current.length === 0 && currentKeys.length > 0) {
      originalOrderRef.current = [...currentKeys];
    }

    // Use the original order if available, otherwise use current keys
    const keysToProcess =
      originalOrderRef.current.length > 0
        ? originalOrderRef.current
        : currentKeys;

    keysToProcess.forEach((espKey) => {
      const data = firebaseData.espToEsp[espKey];

      if (!data) return;

      // Split the key like "gmail-microsoft" into sender and recipient
      const [senderEsp, recipientEsp] = espKey.split('-');

      if (senderEsp && recipientEsp) {
        // Calculate total count - handle null values
        const totalCount =
          (data.inboxCount || 0) +
          (data.spamCount || 0) +
          (data.otherCount || 0) +
          (data.notDeliveredCount || 0);

        // Calculate percentages using helper function
        const inboxPercentage = calculatePercentage(
          data.inboxCount,
          totalCount,
        );
        const spamPercentage = calculatePercentage(data.spamCount, totalCount);
        const otherPercentage = calculatePercentage(
          data.otherCount,
          totalCount,
        );
        const notDeliveredPercentage = calculatePercentage(
          data.notDeliveredCount,
          totalCount,
        );

        firebaseRows.push({
          senderEmailType: senderEsp,
          recipientEmailType: recipientEsp,
          inbox: { percentage: inboxPercentage },
          spam: { percentage: spamPercentage },
          other: { percentage: otherPercentage },
          notDelivered: { percentage: notDeliveredPercentage },
        });
      }
    });

    // Sort the Firebase rows by priority
    return sortEspPairs(firebaseRows);
  };

  // Check if we should show dashes (sentCount is 0 and Firebase is not connected)
  const shouldShowAsPerSentCount = sentCount === 0 && !firebaseData;

  // Use Firebase data if available, otherwise fall back to API data
  const tableData = useMemo(() => {
    if (shouldShowAsPerSentCount) {
      // Return data with dashes for all percentage values
      const dataWithDashes = (testReportEspsData || []).map((item) => ({
        ...item,
        inbox: { percentage: null },
        spam: { percentage: null },
        other: { percentage: null },
        notDelivered: { percentage: null },
      }));
      return sortEspPairs(dataWithDashes);
    }

    const firebaseTableData = getFirebaseTableData();
    if (firebaseTableData) {
      return firebaseTableData;
    }

    // Sort regular API data by priority
    return sortEspPairs(testReportEspsData || []);
  }, [firebaseData, testReportEspsData, shouldShowAsPerSentCount]);

  return (
    <div className="esp-placement-report-container">
      <span className="esp-placement-report-header">
        ESP-to-ESP Placements Report
      </span>
      <div className="esp-placement-report-table w-100">
        <Table
          columns={[
            {
              dataField: 'senderEmailType',
              text: 'Sender ESP',
              headerAlign: 'start',
              component: (cell, row) => (
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-4">
                    {getEspIcon(row?.senderEmailType)}
                    <span className="email-address">
                      {SeedAccountLabels[row?.senderEmailType]}
                    </span>
                  </div>
                  <ArrowRight width={16} height={16} color="#1f2937" />
                </div>
              ),
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
            {
              dataField: 'recipientEsp',
              text: 'Recipient ESP',
              headerAlign: 'start',
              component: (cell, row) => (
                <div className="d-flex align-items-center gap-4">
                  {getEspIcon(row?.recipientEmailType)}
                  <span className="email-address">
                    {SeedAccountLabels[row?.recipientEmailType]}
                  </span>
                </div>
              ),
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
            {
              dataField: 'inbox',
              text: 'Inbox',
              headerAlign: 'start',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <Inbox width={16} height={16} color="#059669" />
                  <span>Inbox</span>
                </div>
              ),
              component: (cell, row) => {
                const espKey = `${row?.senderEmailType}-${row?.recipientEmailType}`;
                const shouldAnimate = shouldAnimateItem(`${espKey}-inboxCount`);
                return (
                  <span
                    className={shouldAnimate ? 'esp-count-pop-animation' : ''}
                  >
                    {row?.inbox?.percentage !== null
                      ? `${row?.inbox?.percentage}%`
                      : '-'}
                  </span>
                );
              },
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
            {
              dataField: 'spam',
              text: 'Spam',
              headerAlign: 'start',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <AlertTriangle width={16} height={16} color="#DC2626" />
                  <span>Spam</span>
                </div>
              ),
              component: (cell, row) => {
                const espKey = `${row?.senderEmailType}-${row?.recipientEmailType}`;
                const shouldAnimate = shouldAnimateItem(`${espKey}-spamCount`);
                return (
                  <span
                    className={shouldAnimate ? 'esp-count-pop-animation' : ''}
                  >
                    {row?.spam?.percentage !== null
                      ? `${row?.spam?.percentage}%`
                      : '-'}
                  </span>
                );
              },
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
            {
              dataField: 'other',
              text: 'Other',
              headerAlign: 'start',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <Mail width={16} height={16} color="#D97706" />
                  <span>Other</span>
                </div>
              ),
              component: (cell, row) => {
                const espKey = `${row?.senderEmailType}-${row?.recipientEmailType}`;
                const shouldAnimate = shouldAnimateItem(`${espKey}-otherCount`);
                return (
                  <span
                    className={shouldAnimate ? 'esp-count-pop-animation' : ''}
                  >
                    {row?.other?.percentage !== null
                      ? `${row?.other?.percentage}%`
                      : '-'}
                  </span>
                );
              },
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
            {
              dataField: 'notDelivered',
              text: 'Undelivered',
              headerAlign: 'start',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <EmailBlock width={16} height={16} color="#1f2937" />
                  <span>Undelivered</span>
                </div>
              ),
              component: (cell, row) => {
                const espKey = `${row?.senderEmailType}-${row?.recipientEmailType}`;
                const shouldAnimate = shouldAnimateItem(
                  `${espKey}-notDeliveredCount`,
                );
                return (
                  <span
                    className={shouldAnimate ? 'esp-count-pop-animation' : ''}
                  >
                    {row?.notDelivered?.percentage !== null
                      ? `${row?.notDelivered?.percentage}%`
                      : '-'}
                  </span>
                );
              },
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
          ]}
          data={tableData}
          pagination={PaginationShowHide.HIDE}
          paginationOptions={{
            options: {
              limit: tableData?.length || 0,
              page: 1,
              totalElements: tableData?.length || 0,
            },
          }}
        />
      </div>
    </div>
  );
};

export default ESPPlacementReport;
